import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { connectMerchantSocket, type MerchantSocketMessage } from '../../../utils/merchantWebSocket';

type MerchantRealtimeContextValue = {
  subscribe: (listener: (event: MerchantSocketMessage) => void) => () => void;
};

const MerchantRealtimeContext = createContext<MerchantRealtimeContextValue>({
  subscribe: () => () => undefined
});

export function MerchantRealtimeProvider({
  token,
  children
}: {
  token?: string;
  children: React.ReactNode;
}) {
  const listenersRef = useRef(new Set<(event: MerchantSocketMessage) => void>());
  const subscribe = useCallback((listener: (event: MerchantSocketMessage) => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  useEffect(() => {
    const accessToken = token?.trim() || '';
    if (!accessToken) return;
    const emit = (event: MerchantSocketMessage) => {
      listenersRef.current.forEach((listener) => listener(event));
    };
    const closeEmail = connectMerchantSocket({
      path: '/api/v1/email/ws',
      token: accessToken,
      onEvent: emit,
      onReconnect: () => emit({ event: 'EMAIL_SOCKET_RECONNECTED' })
    });
    const closeDm = connectMerchantSocket({
      path: '/api/v1/dm/ws',
      token: accessToken,
      onEvent: emit,
      onReconnect: () => emit({ event: 'DM_SOCKET_RECONNECTED' })
    });
    return () => {
      closeEmail();
      closeDm();
    };
  }, [token]);

  const value = useMemo(() => ({ subscribe }), [subscribe]);
  return <MerchantRealtimeContext.Provider value={value}>{children}</MerchantRealtimeContext.Provider>;
}

export function useMerchantRealtime(onEvent: (event: MerchantSocketMessage) => void) {
  const { subscribe } = useContext(MerchantRealtimeContext);
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;
  useEffect(() => subscribe((event) => handlerRef.current(event)), [subscribe]);
}
